import { useState } from 'react';
import { clsx } from 'clsx';

import type { ReactNode } from 'react';

import styles from './CheckboxButtonBar.module.css';

import { Label } from 'components/Label';
import { FeedbackBox } from 'components/FeedbackBox';
import { ControlsButton } from 'components/ControlsButton';

type CheckboxProps = {
  label: string | ReactNode;
  value: string;
  prefix?: string;
  tooltip?: ReactNode;
  tooltipPosition?: 'left' | 'center' | 'right';
  disabled?: boolean;
};

export interface CheckboxButtonBarProps {
  /** array with objects with label, value, prefix and disabled*/
  checkboxes: CheckboxProps[];
  //** array with value(s) */
  defaultValues?: string[];
  label?: string;
  /** optional sub-label text (only displayed when label is positioned 'top') */
  subLabel?: string;
  /** 'top' | 'left' */
  labelPosition?: 'top' | 'left';
  /** `true` to show an asterisk to indicate an item is required; otherwise, `false`. */
  required?: boolean;
  /** `true` to color border red to show not valid; otherwise, `false`. */
  notValid?: boolean;
  /** error text to display under the bar */
  notValidatedText?: string;
  /** returns an array with only one value */
  actAsRadioButtonBar?: boolean;
  /** optional footer text */
  footerText?: string;
  /** disables all checkboxes in the bar */
  disableGroup?: boolean;
  /** `true` to sepearte each button in their own bordered container */
  segmented?: boolean;
  /** `true` sets forceSquared on all buttons */
  forceSquared?: boolean;
  onSetValues: (value: string[]) => void;
}

export const CheckboxButtonBar = ({
  checkboxes,
  defaultValues,
  label,
  subLabel = '',
  labelPosition = 'top',
  required = false,
  notValid = false,
  notValidatedText = '',
  actAsRadioButtonBar = false,
  footerText = '',
  disableGroup = false,
  segmented = false,
  forceSquared = false,
  onSetValues,
}: CheckboxButtonBarProps) => {
  const [currentValues, setCurrentValues] = useState<string[]>(
    defaultValues && !actAsRadioButtonBar
      ? defaultValues.map(String)
      : defaultValues && actAsRadioButtonBar
        ? defaultValues.slice(0, 1)
        : []
  );

  const handleChange = (value: string) => {
    let values = currentValues;

    if (actAsRadioButtonBar) {
      setCurrentValues([value]);
      onSetValues([value]);
    } else {
      if (values.includes(value)) {
        values = values.filter((val) => val !== value);
      } else {
        values = [...values, value];
      }
      setCurrentValues(values);
      onSetValues(values);
    }
  };

  return (
    <div
      className={clsx(
        styles['checkbox-button-bar__container'],
        styles[`checkbox-button-bar__container--label-${labelPosition}`]
      )}
    >
      {(label || subLabel) && (
        <div>
          {label && (
            <Label htmlFor="" required={required}>
              {label}
            </Label>
          )}

          {subLabel && labelPosition === 'top' && (
            <div className={styles['checkbox-button-bar__sub-text']}>{subLabel}</div>
          )}
        </div>
      )}

      <div
        className={clsx(styles['checkbox-button-bar__row-container'], {
          [styles['checkbox-button-bar__row-container--segmented']]: segmented,
        })}
      >
        {segmented ? (
          checkboxes.map(({ label, value, prefix, tooltip, tooltipPosition, disabled }) => (
            <div
              key={value}
              className={clsx(styles['checkbox-button-bar__row'], {
                [styles['checkbox-button-bar__row--not-valid']]: notValid || notValidatedText,
              })}
            >
              <ControlsButton
                type={actAsRadioButtonBar ? 'radio' : 'checkbox'}
                label={label}
                prefix={prefix}
                tooltip={tooltip}
                tooltipPosition={tooltipPosition}
                forceSquared={forceSquared}
                value={value}
                checked={currentValues.includes(value)}
                disabled={disabled || disableGroup}
                onSetValue={(event) => handleChange(event.target.value)}
              />
            </div>
          ))
        ) : (
          <div
            className={clsx(styles['checkbox-button-bar__row'], {
              [styles['checkbox-button-bar__row--not-valid']]: notValid || notValidatedText,
            })}
          >
            {checkboxes.map(({ label, value, prefix, tooltip, tooltipPosition, disabled }) => (
              <ControlsButton
                key={value}
                type={actAsRadioButtonBar ? 'radio' : 'checkbox'}
                label={label}
                prefix={prefix}
                tooltip={tooltip}
                tooltipPosition={tooltipPosition}
                forceSquared={forceSquared}
                value={value}
                checked={currentValues.includes(value)}
                disabled={disabled || disableGroup}
                onSetValue={(event) => handleChange(event.target.value)}
              />
            ))}
          </div>
        )}
      </div>

      {footerText && <div className={styles['checkbox-button-bar__sub-text']}>{footerText}</div>}
      {notValidatedText !== '' && <FeedbackBox level="error" feedback={notValidatedText} />}
    </div>
  );
};
